import type { Maybe } from '@adornis/base/utilTypes.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { searchProducts } from '../../api/searchProducts.js';
import { Product } from '../../db/Product.js';
import type { ExtractedProductInfo } from '../../features/information-extraction/db/ExtractedProductInfo.js';
import type { AbstractMailExtraction } from '../../features/information-extraction/db/MailExtractionResult.js';

interface ProductEditRowProps {
  product: ExtractedProductInfo;
  mailProcessing: AbstractMailExtraction;
  onSave: () => void;
  onClose: () => void;
}

const LENGTH_TYPES = ['HL', 'ML', 'FX'];
const CERTIFICATES = ['Z_22', 'Z_31'];

export const ProductEditRow = stateful((props: ProductEditRowProps) => {
  const { product, mailProcessing, onSave, onClose } = props;

  const [correctedId, setCorrectedId] = useState<Maybe<string>>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isNewCorrection, setIsNewCorrection] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setIsInitializing(true);
      const hadExistingCorrection = !!mailProcessing.extractionResult?.correctedProducts?.find(
        c => c.extractedProductInfoId === product._id || c._id === product._id,
      );
      const id = await mailProcessing.correctProduct(product);
      if (!cancelled) {
        setCorrectedId(id);
        setIsNewCorrection(!hadExistingCorrection);
        setIsInitializing(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [product._id]);

  if (isInitializing || !correctedId) {
    return html`<div class="flex items-center gap-2 p-4">
      <span class="loading loading-spinner loading-sm"></span>
      <span class="text-sm text-base-content/60">Lade Korrektur...</span>
    </div>`;
  }

  const correctedProduct = mailProcessing.extractionResult?.correctedProducts?.find(p => p._id === correctedId);
  if (!correctedProduct) {
    return html`<div class="text-error p-4">Korrekturprodukt nicht gefunden</div>`;
  }

  const { field } = useForm(correctedProduct, {}, [correctedId, version]);

  const handleProductSelected = (selectedProduct: Product | null) => {
    if (!selectedProduct) return;

    correctedProduct.productNumber = selectedProduct.materialNumber;
    correctedProduct.productGroup = selectedProduct.productGroup;
    correctedProduct.material = selectedProduct.material;
    correctedProduct.deliveryCondition = selectedProduct.deliveryCondition;
    correctedProduct.outerDiameter1 = selectedProduct.outerDiameterOne;
    correctedProduct.outerDiameter2 = selectedProduct.outerDiameterTwo;
    correctedProduct.wallThickness = selectedProduct.wallThickness;
    correctedProduct.innerDiameter = selectedProduct.innerDiameter;
    setVersion(version + 1);
  };

  const handleCancel = () => {
    if (isNewCorrection && correctedId) {
      mailProcessing.removeCorrection(correctedId);
    }
    onClose();
  };

  const handleRevert = async () => {
    if (!correctedId) return;
    if (correctedProduct.extractedProductInfoId) {
      // Extracted product: discard the manual corrections and revert to the AI original.
      mailProcessing.removeCorrection(correctedId);
      await mailProcessing.save();
    } else {
      // Manually added product: soft-remove so it stays reversible via "Entfernte Positionen".
      await mailProcessing.removeProduct(correctedProduct);
    }
    onSave();
  };

  const handleSave = async () => {
    await mailProcessing.save();
    onSave();
  };

  const formatDimensions = (p: Product) => {
    const parts: string[] = [];
    if (p.outerDiameterOne != null) parts.push(`ØAD ${p.outerDiameterOne}`);
    if (p.outerDiameterTwo != null) parts.push(`ØAD2 ${p.outerDiameterTwo}`);
    if (p.wallThickness != null) parts.push(`Wand ${p.wallThickness}`);
    if (p.innerDiameter != null) parts.push(`ØID ${p.innerDiameter}`);
    if (p.hlMin != null || p.hlMax != null) parts.push(`HL ${p.hlMin ?? '?'}–${p.hlMax ?? '?'}`);
    return parts.join(' | ');
  };

  return html`
    <div class="bg-base-200/50 border border-base-300 border-t-2 border-t-primary rounded-lg p-5 space-y-4">
      <!-- Produkt-Picker -->
      <div>
        <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-1 block">
          Produkt aus Stammdaten
        </label>
        ${DropdownSelectionInput<Product>({
          search: async (searchString: string) => {
            const results = await searchProducts(searchString)({
              _id: 1,
              materialNumber: 1,
              productGroupNumber: 1,
              productGroup: 1,
              material: 1,
              deliveryCondition: 1,
              outerDiameterOne: 1,
              outerDiameterTwo: 1,
              wallThickness: 1,
              innerDiameter: 1,
              hlMin: 1,
              hlMax: 1,
            });
            return results ?? [];
          },
          renderString: (p: Product) => `${p.materialNumber ?? '–'} – ${p.material ?? '–'} (${p.productGroup ?? '–'})`,
          renderItem: (p: Product) => html`
            <div class="py-1">
              <div>${p.materialNumber ?? '–'} – ${p.material ?? '–'} (${p.productGroup ?? '–'})</div>
              <div class="text-xs text-base-content/50">${formatDimensions(p) || 'Keine Abmessungen'}</div>
            </div>
          `,
          resolveID: (p: Product) => p._id,
          placeholder: 'Produkt suchen (Mat.-Nr., Material, Warengruppe...)',
          onPicked: handleProductSelected,
        })}
      </div>

      <!-- Stammdaten-Felder (read-only) -->
      <div>
        <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-1 block">
          Produktdaten (aus Stammdaten)
        </label>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          ${TextInput({ ...(field('productNumber') as any), placeholder: 'Mat.-Nr.', disabled: true })}
          ${TextInput({ ...(field('productGroup') as any), placeholder: 'Warengruppe', disabled: true })}
          ${TextInput({ ...(field('material') as any), placeholder: 'Material', disabled: true })}
          ${TextInput({ ...(field('deliveryCondition') as any), placeholder: 'Lieferbedingung', disabled: true })}
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
          ${NumberInput({
            ...(field('outerDiameter1') as any),
            placeholder: 'Ø AD 1',
            suffix: html`mm`,
            disabled: true,
          })}
          ${NumberInput({
            ...(field('outerDiameter2') as any),
            placeholder: 'Ø AD 2',
            suffix: html`mm`,
            disabled: true,
          })}
          ${NumberInput({
            ...(field('wallThickness') as any),
            placeholder: 'Wandstärke',
            suffix: html`mm`,
            disabled: true,
          })}
          ${NumberInput({
            ...(field('innerDiameter') as any),
            placeholder: 'Ø Innen',
            suffix: html`mm`,
            disabled: true,
          })}
        </div>
      </div>

      <!-- Editierbare Felder -->
      <div>
        <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-1 block">
          Auftragsdaten
        </label>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          ${NumberInput({ ...(field('quantity') as any), placeholder: 'Menge' })}
          ${NumberInput({ ...(field('length') as any), placeholder: 'Länge', suffix: html`mm` })}
          ${DropdownSelectionInput<string>({
            ...(field('lengthType') as any),
            selectables: LENGTH_TYPES,
            placeholder: 'Längenart',
            clearable: true,
          })}
          ${DropdownSelectionInput<string>({
            ...(field('certificate') as any),
            selectables: CERTIFICATES,
            placeholder: 'Zertifikat',
            clearable: true,
          })}
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
          ${TextInput({ ...(field('requestID') as any), placeholder: 'Bestell-Nr.' })}
          ${TextInput({ ...(field('positionNumber') as any), placeholder: 'Pos.-Nr.' })}
          ${NumberInput({ ...(field('tolerance') as any), placeholder: 'Toleranz' })}
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
          ${NumberInput({ ...(field('minLength') as any), placeholder: 'Min. Länge', suffix: html`mm` })}
          ${NumberInput({ ...(field('maxLength') as any), placeholder: 'Max. Länge', suffix: html`mm` })}
          ${CheckboxInput({ ...(field('isAlternative') as any), label: 'Alternative' })}
        </div>
        ${correctedProduct.isAlternative
          ? html`
              <div class="grid grid-cols-2 gap-3 mt-2">
                ${TextInput({ ...(field('alternativeFor') as any), placeholder: 'Alternative für Position' })}
              </div>
            `
          : nothing}
      </div>

      <!-- Buttons -->
      <div class="flex gap-2 justify-end pt-2 border-t border-base-300">
        ${!isNewCorrection
          ? correctedProduct.extractedProductInfoId
            ? html`<div class="tooltip tooltip-top mr-auto" data-tip="Setzt alle Änderungen auf den ursprünglich extrahierten Wert zurück">
                <button class="btn btn-warning btn-sm gap-1" @click=${handleRevert}>
                  <x-icon>undo</x-icon> Korrektur zurücksetzen
                </button>
              </div>`
            : html`<button class="btn btn-error btn-sm gap-1 mr-auto" @click=${handleRevert}>
                <x-icon>delete</x-icon> Produkt entfernen
              </button>`
          : nothing}
        <button class="btn btn-ghost btn-sm gap-1" @click=${handleCancel}><x-icon>close</x-icon> Abbrechen</button>
        <button class="btn btn-primary btn-sm gap-1" @click=${handleSave}><x-icon>check</x-icon> Speichern</button>
      </div>
    </div>
  `;
});
